import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Card, CardContent, Typography, Chip, Alert,
  CircularProgress, LinearProgress, Button, Tooltip, Skeleton,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, Refresh as RefreshIcon,
  CheckCircle, Warning, Shield, Analytics,
} from '@mui/icons-material';
import { XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine } from 'recharts';
import { hedgeDetectionAPI } from '../services/api';

// ============================================================
// Shared styles
// ============================================================
const cardSx = {
  height: '100%',
  borderRadius: 2,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
    transform: 'translateY(-1px)',
  },
};

const glassOverlay = {
  bgcolor: 'rgba(255,255,255,0.12)',
  backdropFilter: 'blur(4px)',
  borderRadius: 1,
};

// ============================================================
// Sub-components
// ============================================================

const ScoreCard = ({ data, t }) => {
  if (!data) return null;
  const score = data.hedge_score || 0;
  const signals = data.signals || {};

  const spotSignals = ['below_ema20', 'below_ll10', 'trend_down', 'spot_below_ema50'];
  const derivSignals = ['short_build', 'long_liquidation', 'basis_worsen_2', 'oi_shock_up'];
  const riskSignals = ['close_weak', 'vol_rising', 'sharp_drop'];
  const spotWeights = { below_ema20: 15, below_ll10: 15, trend_down: 10, spot_below_ema50: 10 };
  const derivWeights = { short_build: 25, long_liquidation: 15, basis_worsen_2: 10, oi_shock_up: 5 };
  const riskWeights = { close_weak: 10, vol_rising: 10, sharp_drop: 10 };

  const spotScore = spotSignals.reduce((sum, s) => sum + (signals[s] ? spotWeights[s] : 0), 0);
  const derivScore = derivSignals.reduce((sum, s) => sum + (signals[s] ? derivWeights[s] : 0), 0);
  const riskScore = riskSignals.reduce((sum, s) => sum + (signals[s] ? riskWeights[s] : 0), 0);
  const allScoringSignals = [...spotSignals, ...derivSignals, ...riskSignals];
  const totalActive = allScoringSignals.filter(s => signals[s]).length;
  const totalSignals = allScoringSignals.length;

  const getGradient = () => {
    if (score >= 70) return 'linear-gradient(135deg, #c62828 0%, #e53935 100%)';
    if (score >= 50) return 'linear-gradient(135deg, #e65100 0%, #ff6d00 100%)';
    return 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)';
  };

  return (
    <Card sx={{ ...cardSx, background: getGradient(), '&:hover': { ...cardSx['&:hover'], boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}>
      <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem' }}>{t('hedgeDetection.score.title')}</Typography>
          <Chip label={data.trade_date} size="small" sx={{ ...glassOverlay, color: 'white', fontWeight: 600, fontSize: '0.68rem', height: 22 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flex: 1 }}>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '2.8rem', lineHeight: 1 }}>{score}</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: '1.1rem' }}>/100</Typography>
        </Box>
        <LinearProgress
          variant="determinate" value={score}
          sx={{ my: 1.5, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: 'white', borderRadius: 3 } }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[
            { label: t('hedgeDetection.score.spot'), val: spotScore, max: 50 },
            { label: t('hedgeDetection.score.deriv'), val: derivScore, max: 40 },
            { label: t('hedgeDetection.score.risk'), val: riskScore, max: 30 },
            { label: t('hedgeDetection.score.signals'), val: totalActive, max: totalSignals },
          ].map(item => (
            <Box key={item.label} sx={{ flex: 1, p: 0.75, ...glassOverlay, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center', minHeight: 48 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: '0.55rem', letterSpacing: '0.5px', minHeight: '2em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.label}</Typography>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.3 }}>
                {item.val}<Typography component="span" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>/{item.max}</Typography>
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const ZoneCard = ({ data, t }) => {
  if (!data) return null;
  const zone = data.hedge_zone || 'none';
  const configs = {
    strong: { grad: 'linear-gradient(135deg, #b71c1c 0%, #c62828 100%)', icon: <Warning sx={{ fontSize: 32 }} /> },
    medium: { grad: 'linear-gradient(135deg, #e65100 0%, #ef6c00 100%)', icon: <Shield sx={{ fontSize: 32 }} /> },
    none: { grad: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)', icon: <CheckCircle sx={{ fontSize: 32 }} /> },
  };
  const cfg = configs[zone] || configs.none;

  const aboveEma20 = data.ema20 && data.spot_close ? data.spot_close >= data.ema20 : null;
  const aboveEma50 = data.ema50 && data.spot_close ? data.spot_close >= data.ema50 : null;

  return (
    <Card sx={{ ...cardSx, background: cfg.grad, '&:hover': { ...cardSx['&:hover'], boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}>
      <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem', mb: 1 }}>{t('hedgeDetection.zone.title')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'white', mb: 1 }}>
          {cfg.icon}
          <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.2 }}>{t(`hedgeDetection.zone.${zone}`)}</Typography>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', lineHeight: 1.5, flex: 1 }}>{t(`hedgeDetection.zone.desc_${zone}`)}</Typography>
        {(data.ema20 || data.ema50) && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
            {data.ema20 && (
              <Box sx={{ px: 1, py: 0.4, ...glassOverlay, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {aboveEma20 ? <TrendingUp sx={{ fontSize: 13, color: '#a5d6a7' }} /> : <TrendingDown sx={{ fontSize: 13, color: '#ef9a9a' }} />}
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.62rem' }}>EMA20</Typography>
              </Box>
            )}
            {data.ema50 && (
              <Box sx={{ px: 1, py: 0.4, ...glassOverlay, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {aboveEma50 ? <TrendingUp sx={{ fontSize: 13, color: '#a5d6a7' }} /> : <TrendingDown sx={{ fontSize: 13, color: '#ef9a9a' }} />}
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.62rem' }}>EMA50</Typography>
              </Box>
            )}
            {data.atr14 && (
              <Box sx={{ px: 1, py: 0.4, ...glassOverlay }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600, fontSize: '0.62rem' }}>ATR {data.atr14.toFixed(1)}</Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const ActionCard = ({ data, t }) => {
  if (!data) return null;
  const action = data.action || 'no_hedge';
  const cfgs = {
    enter_hedge_strong: { color: '#c62828', bg: '#ffebee' },
    enter_hedge_medium: { color: '#e65100', bg: '#fff3e0' },
    no_hedge: { color: '#2e7d32', bg: '#e8f5e9' },
  };
  const cfg = cfgs[action] || cfgs.no_hedge;

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem', mb: 1.5 }}>{t('hedgeDetection.action.title')}</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: cfg.color, lineHeight: 1.1, mb: 0.5 }}>{t(`hedgeDetection.action.${action}`)}</Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5, flex: 1 }}>{t(`hedgeDetection.action.desc_${action}`)}</Typography>
        {data.sizing_hint && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1.5 }}>
            <Typography sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.3 }}>{t('hedgeDetection.action.sizing')}</Typography>
            <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.5, color: 'text.primary' }}>{t(`hedgeDetection.sizing.${data.sizing_hint}`)}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const MarketDataCard = ({ data, t }) => {
  if (!data) return null;
  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem', mb: 1.5 }}>{t('hedgeDetection.marketData.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 600 }}>VN30 Spot</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#1565c0', lineHeight: 1.2 }}>{data.spot_close?.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 600 }}>VN30F1M</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#6a1b9a', lineHeight: 1.2 }}>{data.f1_close?.toFixed(2)}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
          <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, bgcolor: data.basis >= 0 ? '#f1f8e9' : '#fce4ec' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.62rem', color: data.basis >= 0 ? '#558b2f' : '#c62828', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('hedgeDetection.marketData.basis')}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: data.basis >= 0 ? '#2e7d32' : '#c62828' }}>
              {data.basis?.toFixed(2)} <Typography component="span" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>({data.basis_pct?.toFixed(2)}%)</Typography>
            </Typography>
          </Box>
          <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, bgcolor: data.d_oi == null ? '#f5f5f5' : data.d_oi >= 0 ? '#e8eaf6' : '#fff3e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.62rem', color: data.d_oi == null ? '#757575' : data.d_oi >= 0 ? '#283593' : '#e65100', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('hedgeDetection.marketData.oiChange')}</Typography>
              {(() => {
                if (data.d_oi == null) return null;
                const signals = data.signals || {};
                const regime = signals.short_build ? { label: t('hedgeDetection.oiRegime.short_build'), color: '#c62828', bg: '#ffebee' }
                  : signals.long_liquidation ? { label: t('hedgeDetection.oiRegime.long_liq'), color: '#e65100', bg: '#fff3e0' }
                  : data.d_oi > 0 && (data.ret || 0) > 0 ? { label: t('hedgeDetection.oiRegime.long_build'), color: '#2e7d32', bg: '#e8f5e9' }
                  : data.d_oi < 0 && (data.ret || 0) > 0 ? { label: t('hedgeDetection.oiRegime.short_cover'), color: '#1565c0', bg: '#e3f2fd' }
                  : null;
                return regime ? <Chip label={regime.label} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, color: regime.color, bgcolor: regime.bg, border: 'none', '& .MuiChip-label': { px: 0.6 } }} /> : null;
              })()}
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: data.d_oi == null ? '#757575' : data.d_oi >= 0 ? '#1565c0' : '#e65100' }}>
              {data.d_oi == null ? '—' : `${data.d_oi >= 0 ? '+' : ''}${data.d_oi.toFixed(0)}`}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const ExitSignalsCard = ({ data, t }) => {
  if (!data) return null;
  const signals = data.signals || {};
  const exitCount = data.exit_signals_count || 0;
  const exitAction = data.exit_action || 'hold_hedge';

  const exitCfgs = {
    exit_hedge: { color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
    reduce_hedge: { color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
    hold_hedge: { color: '#546e7a', bg: '#eceff1', border: '#cfd8dc' },
  };
  const cfg = exitCfgs[exitAction] || exitCfgs.hold_hedge;

  const exitItems = [
    { key: 'basis_improving', label: t('hedgeDetection.exit.basis_improving'), desc: t('hedgeDetection.exit.desc_basis_improving') },
    { key: 'short_cover_detected', label: t('hedgeDetection.exit.short_cover_detected'), desc: t('hedgeDetection.exit.desc_short_cover_detected') },
    { key: 'spot_reclaim_ema20', label: t('hedgeDetection.exit.spot_reclaim_ema20'), desc: t('hedgeDetection.exit.desc_spot_reclaim_ema20') },
    { key: 'pin_bar_reversal', label: t('hedgeDetection.exit.pin_bar_reversal'), desc: t('hedgeDetection.exit.desc_pin_bar_reversal') },
  ];

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{t('hedgeDetection.exit.title')}</Typography>
          <Chip
            label={t(`hedgeDetection.exit.${exitAction}`)}
            size="small"
            sx={{ fontWeight: 700, fontSize: '0.7rem', height: 24, bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {exitItems.map(item => {
            const on = signals[item.key];
            return (
              <Box key={item.key} sx={{
                flex: '1 1 180px',
                display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5,
                borderRadius: 1.5, transition: 'all 0.2s ease',
                bgcolor: on ? '#e8f5e9' : 'transparent',
                border: '1px solid', borderColor: on ? '#a5d6a7' : '#f0f0f0',
                opacity: on ? 1 : 0.7,
              }}>
                {on
                  ? <CheckCircle sx={{ fontSize: 18, color: '#4caf50', flexShrink: 0 }} />
                  : <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #e0e0e0', flexShrink: 0 }} />
                }
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: on ? 700 : 400, fontSize: '0.8rem', lineHeight: 1.2, color: on ? 'text.primary' : 'text.secondary' }}>{item.label}</Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.3 }}>{item.desc}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1.5, border: '1px solid #f0f0f0' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.5 }}>
            <Typography component="span" sx={{ fontWeight: 700 }}>{exitCount}/4</Typography> {t('hedgeDetection.exit.conditionsMet')}
            {' — '}
            <Typography component="span" sx={{ fontWeight: 700, color: cfg.color }}>{t(`hedgeDetection.exit.${exitAction}`)}</Typography>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const HistoryChart = ({ data, t }) => {
  if (!data || data.length === 0) {
    return (
      <Card sx={{ ...cardSx, height: 300 }}>
        <CardContent><Typography variant="h6" color="text.secondary">{t('hedgeDetection.noData')}</Typography></CardContent>
      </Card>
    );
  }
  const chartData = [...data].reverse().map((d, i, arr) => ({
    date: d.trade_date,
    score: d.hedge_score,
    zone: d.hedge_zone,
    action: d.action,
    spot: d.spot_close,
    delta: i > 0 ? d.hedge_score - arr[i - 1].hedge_score : 0,
  }));

  return (
    <Card sx={{ ...cardSx, height: 300 }}>
      <CardContent sx={{ height: '100%', p: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 1 }}>{t('hedgeDetection.chart.title')}</Typography>
        <ResponsiveContainer width="100%" height="88%">
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="colorHedgeScore" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e53935" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#e53935" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#999' }} tickLine={false} axisLine={{ stroke: '#e0e0e0' }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#999' }} tickLine={false} axisLine={false} />
            <ReferenceLine y={70} stroke="#c62828" strokeDasharray="4 4" strokeOpacity={0.5} />
            <ReferenceLine y={50} stroke="#ff9800" strokeDasharray="4 4" strokeOpacity={0.5} />
            <RTooltip
              wrapperStyle={{ zIndex: 10 }}
              cursor={{ stroke: '#e53935', strokeWidth: 1, strokeDasharray: '4 4' }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const d = payload[0].payload;
                  const scoreColor = d.score >= 70 ? '#c62828' : d.score >= 50 ? '#e65100' : '#2e7d32';
                  return (
                    <Box sx={{ minWidth: 170, borderRadius: 2, bgcolor: '#ffffff', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', border: '1px solid #eee', overflow: 'hidden' }}>
                      <Box sx={{ px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 500 }}>{d.date}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.8rem', color: scoreColor, fontWeight: 800 }}>{d.score}/100</Typography>
                          {d.delta !== 0 && <Typography sx={{ fontSize: '0.65rem', color: d.delta > 0 ? '#c62828' : '#2e7d32', fontWeight: 700 }}>{d.delta > 0 ? '+' : ''}{d.delta}</Typography>}
                        </Box>
                      </Box>
                      <Box sx={{ px: 1.5, py: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{t('hedgeDetection.chart.tooltipAction')}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{t(`hedgeDetection.action.${d.action}`)}</Typography>
                        </Box>
                        {d.spot && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>VN30</Typography>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{d.spot?.toFixed(1)}</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                }
                return null;
              }}
            />
            <Area type="monotone" dataKey="score" stroke="#e53935" fill="url(#colorHedgeScore)" strokeWidth={2.5} dot={{ r: 3, fill: '#e53935', strokeWidth: 0 }} activeDot={{ r: 5, fill: '#e53935', stroke: 'white', strokeWidth: 2 }} />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

const StatsCard = ({ stats, t }) => {
  if (!stats) return null;

  const zoneColors = { strong: '#c62828', medium: '#e65100', none: '#2e7d32' };
  const actionColors = { enter_hedge_strong: '#c62828', enter_hedge_medium: '#e65100', no_hedge: '#2e7d32' };

  const total = stats.total_signals || 1;
  const zones = stats.zones || [];
  const actions = stats.actions || {};

  return (
    <Card sx={{ ...cardSx, height: 300 }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{t('hedgeDetection.stats.title')}</Typography>
          <Chip label={`${stats.total_signals} ${t('hedgeDetection.stats.days')}`} size="small" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22, bgcolor: '#f5f5f5' }} />
        </Box>

        {/* Average Score */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fafafa', borderRadius: 1.5, border: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
            <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>{t('hedgeDetection.stats.avgScore')}</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: stats.avg_score >= 50 ? '#c62828' : 'text.primary', lineHeight: 1 }}>{stats.avg_score}</Typography>
          </Box>
          <LinearProgress
            variant="determinate" value={stats.avg_score || 0}
            sx={{ height: 5, borderRadius: 3, bgcolor: '#e8e8e8', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #e53935, #c62828)', borderRadius: 3 } }}
          />
        </Box>

        {/* Zone Distribution */}
        <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.5px', mb: 0.75 }}>{t('hedgeDetection.stats.zoneDist')}</Typography>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', height: 16, borderRadius: 2, overflow: 'hidden', mb: 0.75 }}>
            {zones.map(z => (
              <Tooltip key={z.hedge_zone} title={`${t(`hedgeDetection.zone.${z.hedge_zone}`)}: ${z.count} ${t('hedgeDetection.stats.days')}`} arrow>
                <Box sx={{ width: `${(z.count / total) * 100}%`, bgcolor: zoneColors[z.hedge_zone] || '#999', minWidth: z.count > 0 ? 6 : 0, transition: 'width 0.3s' }} />
              </Tooltip>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {zones.map(z => (
              <Box key={z.hedge_zone} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: zoneColors[z.hedge_zone] || '#999' }} />
                <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>{t(`hedgeDetection.zone.${z.hedge_zone}`)} ({z.count})</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Action Distribution */}
        <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.5px', mb: 0.75 }}>{t('hedgeDetection.stats.actionDist')}</Typography>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
          {[
            { key: 'no_hedge', label: t('hedgeDetection.stats.noHedge') },
            { key: 'enter_hedge_medium', label: t('hedgeDetection.stats.hedgeMedium') },
            { key: 'enter_hedge_strong', label: t('hedgeDetection.stats.hedgeStrong') },
          ].map(({ key, label }) => {
            const count = actions[key] || 0;
            const pct = total > 0 ? (count / total) * 100 : 0;
            const color = actionColors[key];
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: count > 0 ? 1 : 0.65 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.68rem', width: 90, flexShrink: 0, color: count > 0 ? color : 'text.disabled' }}>{label}</Typography>
                <Box sx={{ flex: 1, height: 14, bgcolor: '#f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ width: `${Math.max(pct, count > 0 ? 8 : 0)}%`, height: '100%', bgcolor: color, borderRadius: 2, transition: 'width 0.4s ease' }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', width: 20, textAlign: 'right', color: count > 0 ? 'text.primary' : 'text.disabled' }}>{count}</Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

const SignalsCard = ({ signals, t }) => {
  if (!signals) return null;

  const spotSignals = [
    { key: 'below_ema20', pts: 15 },
    { key: 'below_ll10', pts: 15 },
    { key: 'trend_down', pts: 10 },
    { key: 'spot_below_ema50', pts: 10 },
  ];
  const derivSignals = [
    { key: 'short_build', pts: 25 },
    { key: 'long_liquidation', pts: 15 },
    { key: 'basis_worsen_2', pts: 10 },
    { key: 'oi_shock_up', pts: 5 },
  ];
  const riskSignals = [
    { key: 'close_weak', pts: 10 },
    { key: 'vol_rising', pts: 10 },
    { key: 'sharp_drop', pts: 10 },
  ];

  const allSignals = [...spotSignals, ...derivSignals, ...riskSignals];
  const totalActive = allSignals.filter(s => signals[s.key]).length;
  const totalAll = allSignals.length;

  const renderGroup = (titleKey, items, maxPts) => {
    const groupPts = items.reduce((sum, s) => sum + (signals[s.key] ? s.pts : 0), 0);
    return (
      <Box sx={{ flex: '1 1 0', minWidth: 200 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: '1px solid #f0f0f0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{t(titleKey)}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: groupPts > 0 ? '#e53935' : 'text.disabled' }}>{groupPts}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>/{maxPts} pts</Typography>
          </Box>
        </Box>
        {items.map(s => {
          const on = signals[s.key];
          return (
            <Box key={s.key} sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, py: 0.85, px: 1.5, mb: 0.5,
              borderRadius: 1.5, transition: 'all 0.2s ease',
              bgcolor: on ? '#ffebee' : 'transparent',
              border: '1px solid', borderColor: on ? '#ef9a9a' : '#f0f0f0',
              opacity: on ? 1 : 0.7,
            }}>
              {on
                ? <Warning sx={{ fontSize: 18, color: '#e53935', flexShrink: 0 }} />
                : <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #e0e0e0', flexShrink: 0 }} />
              }
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: on ? 700 : 400, fontSize: '0.8rem', lineHeight: 1.2, color: on ? 'text.primary' : 'text.secondary' }}>{t(`hedgeDetection.signals.${s.key}`)}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.3 }}>{t(`hedgeDetection.signals.desc_${s.key}`)}</Typography>
              </Box>
              <Box sx={{ px: 0.75, py: 0.25, borderRadius: 1, bgcolor: on ? '#ffcdd2' : '#fafafa' }}>
                <Typography sx={{ fontWeight: 700, color: on ? '#c62828' : '#ccc', fontSize: '0.68rem' }}>+{s.pts}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{t('hedgeDetection.signals.title')}</Typography>
          <Chip
            label={`${totalActive}/${totalAll} ${t('hedgeDetection.signals.active')}`}
            size="small"
            sx={{
              fontWeight: 700, fontSize: '0.7rem', height: 24,
              bgcolor: totalActive >= 5 ? '#ffebee' : totalActive >= 3 ? '#fff3e0' : '#f5f5f5',
              color: totalActive >= 5 ? '#c62828' : totalActive >= 3 ? '#e65100' : 'text.secondary',
              border: '1px solid',
              borderColor: totalActive >= 5 ? '#ef9a9a' : totalActive >= 3 ? '#ffcc80' : '#e0e0e0',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {renderGroup('hedgeDetection.signals.spotTitle', spotSignals, 50)}
          {renderGroup('hedgeDetection.signals.derivTitle', derivSignals, 40)}
          {renderGroup('hedgeDetection.signals.riskTitle', riskSignals, 30)}
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================================
// Main Page
// ============================================================

function HedgeDetection() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [latestSignal, setLatestSignal] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [latestRes, historyRes, statsRes] = await Promise.all([
        hedgeDetectionAPI.getLatest(),
        hedgeDetectionAPI.getHistory(30),
        hedgeDetectionAPI.getStats(30),
      ]);
      setLatestSignal(latestRes.data);
      setHistory(historyRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const handleCalculate = async () => {
    setCalculating(true);
    setError(null);
    try {
      await hedgeDetectionAPI.calculate({
        index_code: 'VN30',
        futures_symbol: 'VN30F1M',
        trading_days: 90
      });
      // Refresh data after calculation
      await fetchData();
    } catch (err) {
      console.error('Calculate error:', err);
      setError(err.response?.data?.detail || 'Failed to calculate');
    } finally {
      setCalculating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        {/* Header skeleton */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '12px' }} />
            <Box>
              <Skeleton width={220} height={24} />
              <Skeleton width={200} height={14} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: '4px' }} />
            <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: '4px' }} />
          </Box>
        </Box>

        {/* Alert banner skeleton */}
        <Skeleton variant="rounded" height={64} sx={{ borderRadius: 2, mb: 2.5 }} />

        {/* ── Main Stats Row: 4 cards ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
          {/* ScoreCard skeleton */}
          <Box sx={{ flex: '1 1 0', minWidth: 220 }}>
            <Card sx={{ ...cardSx, background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Skeleton width={100} height={12} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                  <Skeleton variant="rounded" width={75} height={22} sx={{ borderRadius: 3, bgcolor: 'rgba(255,255,255,0.2)' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
                  <Skeleton width={60} height={50} sx={{ bgcolor: 'rgba(255,255,255,0.25)' }} />
                  <Skeleton width={30} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.15)' }} />
                </Box>
                <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3, my: 1.5, bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {[0, 1, 2, 3].map(i => (
                    <Box key={i} sx={{ flex: 1, p: 0.75, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 1, textAlign: 'center' }}>
                      <Skeleton width="80%" height={9} sx={{ mx: 'auto', mb: 0.3, bgcolor: 'rgba(255,255,255,0.15)' }} />
                      <Skeleton width="60%" height={14} sx={{ mx: 'auto', bgcolor: 'rgba(255,255,255,0.2)' }} />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* ZoneCard skeleton */}
          <Box sx={{ flex: '1 1 0', minWidth: 220 }}>
            <Card sx={{ ...cardSx, background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Skeleton width={90} height={12} sx={{ mb: 1, bgcolor: 'rgba(255,255,255,0.3)' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Skeleton width={120} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.25)' }} />
                </Box>
                <Skeleton width="90%" height={12} sx={{ mb: 0.5, bgcolor: 'rgba(255,255,255,0.15)' }} />
                <Skeleton width="70%" height={12} sx={{ mb: 1.5, bgcolor: 'rgba(255,255,255,0.15)' }} />
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.15)' }} />
                  <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.15)' }} />
                  <Skeleton variant="rounded" width={55} height={24} sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.15)' }} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* ActionCard skeleton */}
          <Box sx={{ flex: '1 1 0', minWidth: 220 }}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: 2.5 }}>
                <Skeleton width={100} height={12} sx={{ mb: 1.5 }} />
                <Skeleton width={160} height={28} sx={{ mb: 0.5 }} />
                <Skeleton width="90%" height={12} sx={{ mb: 0.3 }} />
                <Skeleton width="75%" height={12} sx={{ mb: 1.5 }} />
                <Box sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1.5 }}>
                  <Skeleton width={70} height={10} sx={{ mb: 0.5 }} />
                  <Skeleton width="85%" height={12} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* MarketDataCard skeleton */}
          <Box sx={{ flex: '1 1 0', minWidth: 220 }}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: 2.5 }}>
                <Skeleton width={90} height={12} sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width={55} height={10} sx={{ mb: 0.3 }} />
                    <Skeleton width={80} height={26} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width={60} height={10} sx={{ mb: 0.3 }} />
                    <Skeleton width={80} height={26} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#f1f8e9' }}>
                    <Skeleton width={35} height={10} sx={{ mb: 0.3 }} />
                    <Skeleton width={70} height={18} />
                  </Box>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#e8eaf6' }}>
                    <Skeleton width={55} height={10} sx={{ mb: 0.3 }} />
                    <Skeleton width={50} height={18} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* ── Exit Signals skeleton ── */}
        <Card sx={{ ...cardSx, mb: 2.5 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Skeleton width={150} height={20} />
              <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: 3 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {[0, 1, 2].map(i => (
                <Box key={i} sx={{ flex: '1 1 180px', display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5, borderRadius: 1.5, border: '1px solid #f0f0f0' }}>
                  <Skeleton variant="circular" width={18} height={18} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="70%" height={14} sx={{ mb: 0.3 }} />
                    <Skeleton width="90%" height={10} />
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1.5 }}>
              <Skeleton width={200} height={12} />
            </Box>
          </CardContent>
        </Card>

        {/* ── Charts Row: History + Stats ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
          {/* History chart skeleton */}
          <Box sx={{ flex: '2 1 0', minWidth: 400 }}>
            <Card sx={{ ...cardSx, height: 300 }}>
              <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Skeleton width={180} height={20} sx={{ mb: 1.5 }} />
                <Skeleton variant="rounded" sx={{ flex: 1, borderRadius: 1.5 }} />
              </CardContent>
            </Card>
          </Box>

          {/* Stats card skeleton */}
          <Box sx={{ flex: '1 1 0', minWidth: 300 }}>
            <Card sx={{ ...cardSx, height: 300 }}>
              <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Skeleton width={120} height={20} />
                  <Skeleton variant="rounded" width={65} height={22} sx={{ borderRadius: 3 }} />
                </Box>
                {/* Avg score */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fafafa', borderRadius: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Skeleton width={70} height={12} />
                    <Skeleton width={35} height={24} />
                  </Box>
                  <Skeleton variant="rounded" height={5} sx={{ borderRadius: 3 }} />
                </Box>
                {/* Zone dist */}
                <Skeleton width={100} height={10} sx={{ mb: 0.75 }} />
                <Skeleton variant="rounded" height={16} sx={{ borderRadius: 2, mb: 0.75 }} />
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Skeleton variant="circular" width={7} height={7} />
                      <Skeleton width={60} height={10} />
                    </Box>
                  ))}
                </Box>
                {/* Action dist */}
                <Skeleton width={110} height={10} sx={{ mb: 0.75 }} />
                {[0, 1, 2].map(i => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Skeleton width={80} height={11} />
                    <Skeleton variant="rounded" height={14} sx={{ flex: 1, borderRadius: 2 }} />
                    <Skeleton width={16} height={12} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* ── Signals detail skeleton ── */}
        <Card sx={{ ...cardSx, mb: 2.5 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Skeleton width={160} height={20} />
              <Skeleton variant="rounded" width={85} height={24} sx={{ borderRadius: 3 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {/* 3 signal groups */}
              {[3, 4, 2].map((count, gi) => (
                <Box key={gi} sx={{ flex: '1 1 0', minWidth: 200 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, pb: 1, borderBottom: '1px solid #f0f0f0' }}>
                    <Skeleton width={90} height={16} />
                    <Skeleton width={50} height={14} />
                  </Box>
                  {Array.from({ length: count }).map((_, si) => (
                    <Box key={si} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.85, px: 1.5, mb: 0.5, borderRadius: 1.5, border: '1px solid #f0f0f0' }}>
                      <Skeleton variant="circular" width={18} height={18} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="65%" height={13} sx={{ mb: 0.3 }} />
                        <Skeleton width="85%" height={10} />
                      </Box>
                      <Skeleton variant="rounded" width={28} height={18} sx={{ borderRadius: 1 }} />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* ── Footer skeleton ── */}
        <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #f0f0f0', display: 'flex', gap: 3 }}>
          {[0, 1, 2].map(i => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={8} height={8} />
              <Skeleton width={160} height={12} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}><Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert></Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
            background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(229,57,53,0.25)',
          }}>
            <Shield sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
                {t('hedgeDetection.title')}
              </Typography>
              {latestSignal?.trade_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#e53935', boxShadow: '0 0 6px #e53935' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {latestSignal.trade_date}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
              {t('hedgeDetection.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<Analytics />}
            onClick={handleCalculate}
            disabled={calculating}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              bgcolor: '#e53935', '&:hover': { bgcolor: '#c62828' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {calculating ? t('hedgeDetection.calculating') : t('hedgeDetection.calculateNow')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {refreshing ? t('hedgeDetection.refreshing') : t('hedgeDetection.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Calculating banner */}
      {calculating && (
        <Alert severity="info" variant="outlined" icon={<CircularProgress size={18} />} sx={{ mb: 2.5, borderRadius: 2 }}>
          {t('hedgeDetection.calculatingBanner')}
        </Alert>
      )}

      {/* Note Banner */}
      {latestSignal?.note && (
        <Box sx={{ mb: 2.5 }}>
          <Alert
            severity={latestSignal.hedge_zone === 'strong' ? 'error' : latestSignal.hedge_zone === 'medium' ? 'warning' : 'info'}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.25 }}>{t('hedgeDetection.hedgeNote')}</Typography>
            <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>{t(`hedgeDetection.note.${latestSignal.note}`)}</Typography>
          </Alert>
        </Box>
      )}

      {/* Main Stats Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 0', minWidth: 220 }}><ScoreCard data={latestSignal} t={t} /></Box>
        <Box sx={{ flex: '1 1 0', minWidth: 220 }}><ZoneCard data={latestSignal} t={t} /></Box>
        <Box sx={{ flex: '1 1 0', minWidth: 220 }}><ActionCard data={latestSignal} t={t} /></Box>
        <Box sx={{ flex: '1 1 0', minWidth: 220 }}><MarketDataCard data={latestSignal} t={t} /></Box>
      </Box>

      {/* Exit Signals — only show when hedge is active */}
      {latestSignal && latestSignal.hedge_zone !== 'none' && (
        <Box sx={{ mb: 2.5 }}>
          <ExitSignalsCard data={latestSignal} t={t} />
        </Box>
      )}

      {/* Charts Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '2 1 0', minWidth: 400 }}><HistoryChart data={history} t={t} /></Box>
        <Box sx={{ flex: '1 1 0', minWidth: 300 }}><StatsCard stats={stats} t={t} /></Box>
      </Box>

      {/* Signals Row */}
      {latestSignal?.signals && (
        <Box sx={{ mb: 2.5 }}>
          <SignalsCard signals={latestSignal.signals} t={t} />
        </Box>
      )}

      {/* Footer info */}
      <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #f0f0f0', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Score >= 70', desc: t('hedgeDetection.footer.strong'), color: '#c62828' },
          { label: 'Score 50-69', desc: t('hedgeDetection.footer.medium'), color: '#e65100' },
          { label: 'Score < 50', desc: t('hedgeDetection.footer.none'), color: '#2e7d32' },
        ].map(item => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
            <Typography sx={{ fontSize: '0.75rem' }}><Typography component="span" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{item.label}</Typography> — {item.desc}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default HedgeDetection;
